import { useEffect, useMemo } from 'react';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { formatDateTime } from '@/lib/utils';
import { DataTable } from '@/shared/components/data-table/DataTable';
import type { DataTableColumn } from '@/shared/components/data-table/types';
import { makeFilterStorage } from '@/shared/hooks/usePersistedFilters';
import { useColumnVisibility } from '@/shared/hooks/useColumnVisibility';
import { useMeshTable } from '../hooks/useMeshTable';
import type { MeshNode } from '../types';

function formatWhen(iso: string | null): string {
  return iso ? formatDateTime(iso) : 'never';
}

const COLUMNS: DataTableColumn<MeshNode>[] = [
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: [
      { label: 'Online', value: 'online' },
      { label: 'Offline', value: 'offline' },
    ],
    getValue: (n) => (n.online ? 'online' : 'offline'),
    render: (n) => (
      <span className="flex items-center gap-1.5">
        <span
          className={`h-2 w-2 rounded-full ${n.online ? 'bg-green-500' : 'bg-gray-400'}`}
          aria-hidden
        />
        <span className={n.online ? '' : 'text-muted-foreground'}>{n.online ? 'Online' : 'Offline'}</span>
      </span>
    ),
  },
  {
    key: 'hostname',
    label: 'Hostname',
    isPrimarySearch: true,
    searchPlaceholder: 'Search hostname…',
    getValue: (n) => n.hostname,
    render: (n) => <span className="font-medium">{n.hostname}</span>,
  },
  {
    key: 'tailnetIp',
    label: 'Tailnet IP',
    getValue: (n) => n.tailnetIp || '',
    render: (n) => (
      <span className="font-mono text-xs">{n.tailnetIp || '—'}</span>
    ),
  },
  {
    key: 'ownerUser',
    label: 'Owner',
    getValue: (n) => n.ownerUser || '',
    render: (n) => <span>{n.ownerUser || '—'}</span>,
  },
  {
    key: 'kind',
    label: 'Kind',
    filterType: 'select',
    filterOptions: [
      { label: 'Gateway', value: 'gateway' },
      { label: 'Other', value: 'other' },
    ],
    getValue: (n) => (n.isGateway ? 'gateway' : 'other'),
    render: (n) => (
      <span className={n.isGateway ? 'font-medium' : 'text-muted-foreground'}>
        {n.isGateway ? 'Gateway' : '—'}
      </span>
    ),
  },
  {
    key: 'mac',
    label: 'MAC',
    getValue: (n) => n.mac || '',
    render: (n) => <span className="font-mono text-xs">{n.mac || '—'}</span>,
  },
  {
    key: 'lastSeen',
    label: 'Last seen',
    getValue: (n) => n.lastSeen || '',
    render: (n) => (
      <span className={n.online ? '' : 'text-muted-foreground'}>{formatWhen(n.lastSeen)}</span>
    ),
  },
  {
    key: 'createdAt',
    label: 'Joined',
    getValue: (n) => n.createdAt || '',
    render: (n) => <span className="text-muted-foreground">{formatWhen(n.createdAt)}</span>,
  },
];

export function MeshNodesTable({ nodes, loading }: { nodes: MeshNode[]; loading: boolean }) {
  const storage = useMemo(
    () => makeFilterStorage<{ filters: Record<string, string> }>('atek-mesh-nodes'),
    [],
  );
  const table = useMeshTable({
    items: nodes,
    columns: COLUMNS,
    initialColumnFilters: storage.load().filters || {},
    initialSortKey: 'status',
    initialSortDirection: -1,
  });
  const visibility = useColumnVisibility('atek-mesh-nodes-columns');
  useEffect(() => {
    storage.save({ filters: table.columnFilters });
  }, [storage, table.columnFilters]);

  const visibleColumns = COLUMNS.filter((column) => visibility.isVisible(column.key));
  const onlineCount = nodes.filter((n) => n.online).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {table.total} machines — {onlineCount} online
        </span>
        <div className="flex gap-2">
          {table.isFiltered && (
            <Button variant="outline" size="sm" onClick={table.clear}>
              Clear filters
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="mr-1 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {COLUMNS.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.key}
                  checked={visibility.isVisible(column.key)}
                  onCheckedChange={() => visibility.toggle(column.key)}
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <DataTable
        items={table.sortedItems}
        columns={visibleColumns}
        ctx={undefined}
        columnFilters={table.columnFilters}
        sortKey={table.sortKey}
        sortDirection={table.sortDirection}
        onSort={table.setSort}
        onSetColumnFilter={table.setColumnFilter}
        isLoading={loading}
        emptyState={table.isFiltered ? 'No machines match the filters' : 'No machines on the mesh'}
      />
    </div>
  );
}
